import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

interface OpenChat {
  userId: string;
  userName: string;
  avatarUrl: string | null;
}

interface DirectMessageContextType {
  openChats: OpenChat[];
  openChat: (userId: string, userName: string, avatarUrl: string | null) => void;
  closeChat: (userId: string) => void;
  unreadCounts: Record<string, number>;
  clearUnread: (userId: string) => void;
}

const DirectMessageContext = createContext<DirectMessageContextType | undefined>(undefined);

const MAX_OPEN_CHATS = 3;

export const DirectMessageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [openChats, setOpenChats] = useState<OpenChat[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const openChatsRef = useRef<OpenChat[]>([]);

  useEffect(() => {
    openChatsRef.current = openChats;
  }, [openChats]);

  const openChat = useCallback((userId: string, userName: string, avatarUrl: string | null) => {
    setOpenChats(prev => {
      if (prev.find(c => c.userId === userId)) return prev;
      const next = [...prev, { userId, userName, avatarUrl }];
      if (next.length > MAX_OPEN_CHATS) next.shift();
      return next;
    });
    // Clear unread when opening
    setUnreadCounts(prev => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  }, []);

  const closeChat = useCallback((userId: string) => {
    setOpenChats(prev => prev.filter(c => c.userId !== userId));
  }, []);

  const clearUnread = useCallback((userId: string) => {
    setUnreadCounts(prev => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  }, []);

  // Global realtime subscription for incoming DMs
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('dm-incoming')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'direct_messages',
        filter: `receiver_id=eq.${user.id}`
      }, async (payload) => {
        const msg = payload.new as any;
        const senderId = msg.sender_id;
        
        // Check if chat is already open
        const isOpen = openChatsRef.current.some(c => c.userId === senderId);
        
        if (!isOpen) {
          // Fetch sender info to open popup
          const senderName = msg.sender_name || 'Onbekend';
          // Try to get avatar
          const { data: medewerker } = await supabase
            .from('medewerkers')
            .select('avatar_url')
            .eq('id', senderId)
            .single();
          
          setOpenChats(prev => {
            if (prev.find(c => c.userId === senderId)) return prev;
            const next = [...prev, { userId: senderId, userName: senderName, avatarUrl: medewerker?.avatar_url || null }];
            if (next.length > MAX_OPEN_CHATS) next.shift();
            return next;
          });
        }

        // If chat is not actively focused, increment unread
        if (!isOpen) {
          setUnreadCounts(prev => ({
            ...prev,
            [senderId]: (prev[senderId] || 0) + 1
          }));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  return (
    <DirectMessageContext.Provider value={{ openChats, openChat, closeChat, unreadCounts, clearUnread }}>
      {children}
    </DirectMessageContext.Provider>
  );
};

export const useDirectMessageContext = () => {
  const context = useContext(DirectMessageContext);
  if (!context) throw new Error("useDirectMessageContext must be used within DirectMessageProvider");
  return context;
};
